import { memo } from 'react';

import { ClipStatus, ClipType, type NotificationClip as NotificationClipType } from '@air/api/types';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { ClipIconSize } from '~/types/clip-icon-size';

const NOTIFICATION_CLIP_THUMBNAIL_SIZE = 56;
const UI2026_NOTIFICATION_CLIP_THUMBNAIL_SIZE = 40;

export const notificationClip = tailwindVariants({
  base: 'relative flex ui2026:size-10 h-14 w-14 items-center justify-center overflow-hidden rounded-4 ui2026:rounded-8',
  variants: {
    size: {
      medium: 'ui2026:size-10 h-14 w-14',
      small: 'h-10 w-10',
    },
    status: {
      transcoded: 'bg-transparent',
      default: 'ads:bg-subtle bg-pigeon-50',
    },
  },
});

export type NotificationClipVariant = VariantProps<typeof notificationClip>;

export type NotificationClipProps = Pick<NotificationClipVariant, 'status'> & {
  clip: NotificationClipType;
};

export const NotificationClip = memo(({ clip }: NotificationClipProps) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const thumbnailSize = ui2026 ? UI2026_NOTIFICATION_CLIP_THUMBNAIL_SIZE : NOTIFICATION_CLIP_THUMBNAIL_SIZE;

  return (
    <div
      className={notificationClip({
        status: clip.status === ClipStatus.transcoded ? 'transcoded' : 'default',
      })}
    >
      <ClipThumbnail
        src={clip.assets.image || ''}
        iconSize={ClipIconSize.small}
        type={ClipType.photo}
        ext={clip.ext}
        status={clip.status === ClipStatus.transcoded ? clip.status : ClipStatus.nonTranscodable}
        width={thumbnailSize}
        height={thumbnailSize}
        className="[&>img]:h-[inherit] [&>img]:object-cover"
        thumbnailPreference="default"
      />
    </div>
  );
});

NotificationClip.displayName = 'NotificationClip';
